'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  BlockIcon,
  decodeHtmlPayload,
  looksLikeIconName,
  useMicroSource,
  useBlockPart,
  type MicroContentTypeSummary,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { BlockRoot } from '../_shared/BlockRoot';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { sectionPad, sectionInner, cardReset } from '../_shared/styles';

/**
 * The micro landing's discovery row — legacy-parity port of
 * "Neyi keşfetmek istiyorsun?":
 *
 * <ul>
 *   <li>Sparkle "Keşfet" kicker + big title/subtitle.</li>
 *   <li>Search box on the right — GET form submitting {@code ?q=} to
 *       the all-kinds listing ({@code searchHref}, served by
 *       {@code MicroListingLayout}'s reserved {@code search} slug).</li>
 *   <li>Curated filter pills (kind groups) that filter the tiles
 *       client-side.</li>
 *   <li>Horizontally scrolling per-kind tinted tiles: icon disc,
 *       title, description, item count, chevron disc. First tile in
 *       the unfiltered view carries a "Featured" badge.</li>
 * </ul>
 *
 * <p>Tiles come from {@code MicroSource.types} and link to each
 * kind's listing page via {@code typeHrefTemplate}.
 */
export interface MicroContentTypeGridProps {
  title?: string;
  subtitle?: string;
  typeHrefTemplate?: string;
  /** Where the search box submits ({@code ?q=} rides along). */
  searchHref?: string;
  /** Per-kind re-skin of the tiles — see {@link MicroTileIconOverride}. */
  tileIcons?: MicroTileIconOverride[];
}

/**
 * One page-level override row for a single content kind.
 *
 * <p>Three layers decide what a tile wears, most specific first:
 * <ol>
 *   <li>this block prop — the page author's pick in the designer;</li>
 *   <li>the content type's own {@code iconRef} (Micro → Content types),
 *       which every page shares;</li>
 *   <li>the built-in {@link kindSpec} glyph and hue.</li>
 * </ol>
 * An empty field falls through to the next layer, so a row that only
 * sets {@code color} keeps the built-in glyph.
 */
export interface MicroTileIconOverride {
  /** Content kind this row re-skins (e.g. {@code SHORT_VIDEO}). */
  kind?: string;
  /** Lucide name, inline SVG or image URL — the three shapes {@code BlockIcon} sniffs. */
  icon?: string;
  /** Accent hue for the glyph, the tinted discs and the featured badge. */
  color?: string;
  /** Overrides the tile's title — blank keeps the content type's own
   *  {@code displayName} (Micro → Content types). */
  title?: string;
  /** Marks this tile "Featured" (the accent-bordered tile with the sparkle
   *  badge). When no row sets this, the first tile in the unfiltered view
   *  keeps that role — same as before this field existed. */
  featured?: boolean;
}

// -----------------------------------------------------------------------------
// Kind palette — same hues as the popup chrome's KindBadge so kind
// identity carries across the surface.
//
// `icon` is a lucide id, NOT markup: every tile glyph on the portal now goes
// through the one {@link BlockIcon} path the admin's icon picker writes into,
// so what an operator sees in the picker is literally what ships. These ids
// are only the last-resort default — a content type with an `iconRef`
// (Micro → Content types) or a page-level {@link MicroTileIconOverride}
// never reaches them.
// -----------------------------------------------------------------------------

export interface KindSpec {
  fg: string;
  bg: string;
  icon: string;
}

/** 12% wash of an accent, for the icon disc behind a glyph of that colour. */
function wash(accent: string): string {
  return `color-mix(in srgb, ${accent} 12%, transparent)`;
}

/**
 * Every hue comes from the tenant theme so a rebrand recolours the tiles
 * with the rest of the portal: the brand ramp carries the "learn / do"
 * kinds, success the "read / check" kinds, amber the playful ones.
 */
export function kindSpec(kind: string): KindSpec {
  const brand = 'var(--color-brand-500, #3b5bdb)';
  const brandDeep = 'var(--color-brand-700, #1e3a8a)';
  const brandSoft = 'var(--color-brand-600, #2563eb)';
  const success = 'var(--color-success-500, #16a34a)';
  const successDeep = 'var(--color-success-600, #15803d)';
  const amber = 'var(--color-amber-500, #f59e0b)';
  const pick = (fg: string, icon: string): KindSpec => ({ fg, bg: wash(fg), icon });
  switch (kind) {
    case 'SHORT_VIDEO':
      return pick(brand, 'play');
    case 'SHORTS':
      return pick(brandSoft, 'smartphone');
    case 'QUIZ':
      return pick(brandDeep, 'bar-chart-3');
    case 'WEEKLY_READ':
      return pick(success, 'book-open');
    case 'INTERACTIVE':
      return pick(amber, 'hand');
    case 'INFO_CARDS':
      return pick(brand, 'credit-card');
    case 'CHECKLIST':
      return pick(successDeep, 'list-checks');
    case 'AUDIO':
      return pick(brandSoft, 'headphones');
    case 'DOWNLOAD':
      return pick('var(--color-text-secondary, #64748b)', 'download');
    case 'SCENARIO':
      return pick(brandDeep, 'git-branch');
    case 'LEARNING_PATH':
      return pick(success, 'route');
    case 'BUSINESS_STORY_CARDS':
      return pick(brand, 'gallery-vertical-end');
    case 'POLL':
      return pick(amber, 'bar-chart-3');
    default:
      return {
        fg: 'var(--color-text-secondary, #64748b)',
        bg: 'var(--color-surface-page, #f3f4f6)',
        icon: 'shapes',
      };
  }
}

// Filter pill groups — a UI grouping of kinds, matching the legacy
// design. Pills whose kinds match none of the institution's content
// types hide themselves, so the row never shows dead filters.
// The "play" group maps to nothing: legacy grouped GAME (+POLL) here but
// the games phase hasn't shipped, so the pill stays auto-hidden.
// `labelKey` is resolved at render so a language switch re-labels the row.
const FILTER_GROUPS: { id: string; labelKey: string; kinds: string[] | 'all' }[] = [
  { id: 'all', labelKey: 'microTypeGrid.filter.all', kinds: 'all' },
  {
    id: 'quick-learn',
    labelKey: 'microTypeGrid.filter.quickLearn',
    kinds: ['SHORT_VIDEO', 'SHORTS', 'AUDIO'],
  },
  {
    id: 'practice',
    labelKey: 'microTypeGrid.filter.practice',
    kinds: ['QUIZ', 'INTERACTIVE', 'CHECKLIST', 'SCENARIO'],
  },
  { id: 'play', labelKey: 'microTypeGrid.filter.play', kinds: [] },
  { id: 'paths', labelKey: 'microTypeGrid.filter.paths', kinds: ['LEARNING_PATH'] },
  { id: 'reads', labelKey: 'microTypeGrid.filter.reads', kinds: ['WEEKLY_READ'] },
  {
    id: 'visual',
    labelKey: 'microTypeGrid.filter.visual',
    kinds: ['INFO_CARDS', 'BUSINESS_STORY_CARDS'],
  },
  { id: 'resources', labelKey: 'microTypeGrid.filter.resources', kinds: ['DOWNLOAD'] },
];

export function MicroContentTypeGrid({
  title,
  subtitle,
  typeHrefTemplate = '/lxp/micro-learning/{kindSlug}',
  searchHref = '/lxp/micro-learning/search',
  tileIcons,
}: MicroContentTypeGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const micro = useMicroSource();
  const [filterId, setFilterId] = useState('all');

  const state = useAsyncResource<MicroContentTypeSummary[]>(
    async (signal) => {
      if (!micro) return null;
      const types = await micro.types.list({ signal });
      return types.length > 0 ? types : null;
    },
    [micro],
  );

  const types = state.kind === 'loaded' ? state.data : [];

  // Keyed by kind so a tile costs one lookup. Rows with no kind (a freshly
  // added, still-blank row in the designer) are dropped rather than matched
  // against an empty kind.
  const overrides = useMemo(() => {
    const map = new Map<string, MicroTileIconOverride>();
    for (const row of tileIcons ?? []) {
      const kind = row?.kind?.trim();
      if (kind) map.set(kind, row);
    }
    return map;
  }, [tileIcons]);

  // An admin-picked featured tile wins over the positional default (the
  // first tile in the unfiltered view) — but only once any row actually
  // opts in, so untouched pages keep today's behaviour unchanged.
  const hasExplicitFeatured = useMemo(
    () => (tileIcons ?? []).some((row) => row?.featured === true),
    [tileIcons],
  );

  const visibleGroups = useMemo(
    () =>
      FILTER_GROUPS.filter(
        (g) => g.kinds === 'all' || types.some((t) => (g.kinds as string[]).includes(t.kind)),
      ),
    [types],
  );

  const filtered = useMemo(() => {
    const group = FILTER_GROUPS.find((g) => g.id === filterId);
    if (!group || group.kinds === 'all') return types;
    return types.filter((t) => (group.kinds as string[]).includes(t.kind));
  }, [types, filterId]);

  // Admin-picked featured tiles lead the rail in every filter tab they
  // appear under — otherwise a featured pick buried a few cards in defeats
  // the point of calling it out. The positional default (no row explicitly
  // marked — the first tile in the unfiltered view) still only applies
  // there; it isn't a sensible fallback per-category.
  const orderedFiltered = useMemo(() => {
    if (!hasExplicitFeatured) return filtered;
    const isFeatured = (t: MicroContentTypeSummary) => overrides.get(t.kind)?.featured === true;
    return [...filtered.filter(isFeatured), ...filtered.filter((t) => !isFeatured(t))];
  }, [filtered, hasExplicitFeatured, overrides]);

  if (!micro) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="MicroContentTypeGrid" adapterName="MicroSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={6} layout="grid" minCardWidth={200} aspect="3 / 2" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <EmptyState title={t('microTypeGrid.empty')} />
      </SectionShell>
    );
  }

  const resolvedTitle = title || t('microTypeGrid.title');
  const resolvedSubtitle = subtitle || t('microTypeGrid.subtitle');

  return (
    <BlockRoot as="section" aria-label={resolvedTitle} style={sectionPad}>
      <div style={sectionInner}>
        {/* Header — kicker + title + subtitle on the left, search on the right. */}
        <header
          {...part('header')}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'clamp(16px, 3vw, 32px)',
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 320px' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--color-brand-500, #3b5bdb)',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              <SparkleIcon /> {t('microTypeGrid.kicker')}
            </div>
            <h2
              {...part('title')}
              style={{
                margin: 0,
                fontSize: 'clamp(24px, 3vw, 36px)',
                fontWeight: 700,
                lineHeight: 1.15,
                color: 'var(--color-text-primary, #0f172a)',
              }}
            >
              {resolvedTitle}
            </h2>
            {resolvedSubtitle && (
              <p
                {...part('subtitle')}
                style={{
                  margin: '8px 0 0',
                  fontSize: 'var(--font-size-sm, 14px)',
                  color: 'var(--color-text-secondary, #64748b)',
                }}
              >
                {resolvedSubtitle}
              </p>
            )}
          </div>

          {/* Search — plain GET form so SSR navigation stays canonical;
              lands on the cross-kind listing which owns the results. */}
          <form
            method="get"
            action={searchHref}
            role="search"
            style={{
              flex: '1 1 260px',
              maxWidth: 360,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              background: 'var(--color-surface-card, #fff)',
              border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
              borderRadius: 999,
            }}
          >
            <SearchIcon />
            <input
              type="search"
              name="q"
              placeholder={t('microTypeGrid.search.placeholder')}
              aria-label={t('microTypeGrid.search.label')}
              {...part('search')}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--color-text-primary, #0f172a)',
                minWidth: 0,
              }}
            />
          </form>
        </header>

        {/* Filter pill row */}
        {visibleGroups.length > 1 && (
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}
            {...part('filter')}
          >
            {visibleGroups.map((g, gi) => {
              const active = filterId === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setFilterId(g.id)}
                  aria-pressed={active}
                  {...part('tab', gi)}
                  style={{
                    padding: 'var(--space-1-5, 6px) var(--space-4, 16px)',
                    borderRadius: 999,
                    border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
                    background: active
                      ? 'color-mix(in srgb, var(--color-brand-500, #3b5bdb) 12%, transparent)'
                      : 'var(--color-surface-card, #fff)',
                    borderColor: active ? 'transparent' : undefined,
                    color: active
                      ? 'var(--color-brand-600, #2563eb)'
                      : 'var(--color-text-primary, #0f172a)',
                    font: 'inherit',
                    fontSize: 'var(--font-size-sm, 14px)',
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {t(g.labelKey)}
                </button>
              );
            })}
          </div>
        )}

        {/* Tile rail */}
        {filtered.length > 0 ? (
          <ContentRail
            ariaLabel={resolvedTitle}
            itemWidth="clamp(200px, 22vw, 240px)"
            snap="start"
            dots
            dotLabel={(i, n) => t('microTypeGrid.page', { n: i + 1, total: n })}
            prevLabel={t('shared.nav.previous')}
            nextLabel={t('shared.nav.next')}
          >
            {orderedFiltered.map((typeSummary, i) => {
              const override = overrides.get(typeSummary.kind);
              const featured = hasExplicitFeatured
                ? Boolean(override?.featured)
                : i === 0 && filterId === 'all';
              return (
                <Tile
                  key={typeSummary.kindSlug}
                  type={typeSummary}
                  href={typeHrefTemplate.replace(
                    '{kindSlug}',
                    encodeURIComponent(typeSummary.kindSlug),
                  )}
                  featured={featured}
                  override={override}
                />
              );
            })}
          </ContentRail>
        ) : (
          <p
            {...part('empty')}
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--color-text-secondary, #64748b)',
              fontSize: 'var(--font-size-sm, 14px)',
            }}
          >
            {t('microTypeGrid.emptyCategory')}
          </p>
        )}
      </div>
    </BlockRoot>
  );
}

// -----------------------------------------------------------------------------
// Tile
// -----------------------------------------------------------------------------

function Tile({
  type,
  href,
  featured,
  override,
}: {
  type: MicroContentTypeSummary;
  href: string;
  featured: boolean;
  override?: MicroTileIconOverride;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const spec = kindSpec(type.kind);
  // Three layers, one renderer. This page's pick wins over the content type's
  // own `iconRef` (Micro → Content types), which wins over the kind's default
  // id. The first two are written by the same SidebarIconInput every other
  // block's icon field uses, so either can be a lucide name, inline SVG or an
  // image URL — exactly the three shapes BlockIcon sniffs. Nothing about the
  // glyph is hard-coded in this component any more: change it in the admin and
  // the portal follows.
  const iconValue = override?.icon?.trim() || type.iconRef?.trim() || spec.icon;
  // One accent hue drives the glyph, both tinted discs and the featured
  // chrome. A custom hue has no hand-tuned tint to pair with, so the disc
  // wash is mixed from it at the same ~12% the built-in palette uses.
  const accent = override?.color?.trim() || spec.fg;
  const tint = override?.color?.trim()
    ? `color-mix(in srgb, ${override.color.trim()} 12%, transparent)`
    : spec.bg;
  const displayTitle = override?.title?.trim() || type.displayName;
  return (
    <Card asChild className={featured ? 'ring-0' : undefined}>
      <Link
        href={href}
        style={{
          ...cardReset,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: '24px 20px',
          background: 'var(--color-surface-card, #fff)',
          ...(featured ? { border: `2px solid ${accent}` } : {}),
          borderRadius: 16,
          textDecoration: 'none',
          color: 'inherit',
          minHeight: 220,
          height: '100%',
        }}
      >
        {featured && (
          <span
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 9px',
              borderRadius: 999,
              background: accent,
              color: 'var(--color-text-on-brand, #fff)',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              textTransform: 'uppercase',
            }}
          >
            <SparkleIcon /> {t('microTypeGrid.featured')}
          </span>
        )}

        {/* Icon disc */}
        <div
          aria-hidden="true"
          style={{
            width: 60,
            height: 60,
            borderRadius: 14,
            background: tint,
            color: accent,
            display: 'inline-grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <TileIconGlyph value={iconValue} size={28} />
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--font-size-md, 16px)',
              fontWeight: 700,
              lineHeight: 1.25,
              marginBottom: 6,
              color: 'var(--color-text-primary, #0f172a)',
            }}
          >
            {displayTitle}
          </div>
          {type.description && (
            <div
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary, #64748b)',
                lineHeight: 1.45,
              }}
            >
              {type.description}
            </div>
          )}
          {typeof type.contentCount === 'number' && (
            <div
              style={{
                marginTop: 8,
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
                fontFamily: 'var(--font-family-mono, monospace)',
              }}
            >
              {t('microTypeGrid.itemCount', { count: type.contentCount })}
            </div>
          )}
        </div>

        {/* Chevron disc — bottom-left */}
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: tint,
            color: accent,
            display: 'inline-grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <ArrowRightIcon />
        </div>
      </Link>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// TileIconGlyph — BlockIcon, plus a broken-image fallback
// -----------------------------------------------------------------------------

/**
 * Wraps {@link BlockIcon} to add the one thing it can't: falling back to
 * the default glyph when an external icon URL 404s or otherwise fails to
 * load. {@code BlockIcon} itself stays hook-free (some of its callers —
 * the SiteShell header/megamenu — render without a `'use client'`
 * boundary of their own), so the `onError`-driven fallback lives here
 * instead, in a file that's unconditionally a client component.
 */
function TileIconGlyph({ value, size }: { value: string; size: number }) {
  const [failed, setFailed] = useState(false);
  const decoded = decodeHtmlPayload(value).trim();
  const isNotAnImage = looksLikeIconName(decoded) || /^\s*<svg[\s>]/i.test(decoded);
  if (failed || isNotAnImage) {
    return <BlockIcon value={failed ? undefined : value} size={size} />;
  }
  return (
    <img
      src={decoded}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      style={{ display: 'inline-block', flexShrink: 0, objectFit: 'contain' }}
      onError={() => setFailed(true)}
    />
  );
}

// -----------------------------------------------------------------------------
// Icons (inline SVG, currentColor)
// -----------------------------------------------------------------------------

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2L13.5 8.5 20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5z" />
      <circle cx="19" cy="5" r="1.5" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ color: 'var(--color-text-tertiary, #94a3b8)', flexShrink: 0 }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
